import { useState, useEffect, useCallback, useMemo } from 'react';
import { PListDetailsDto, PListLineDto, PalletSummaryDto } from '../../../../../types/picking';
import { pickingService } from '../../../../../services/pickingService';
import { settingsService } from '../../../../../services/settingsService';
import { itemsService } from '../../../../../services/itemsService';
import { plistOrderService, PListOrderDto } from '../../../../../services/plistOrderService';
import { userProfileService } from '../../../../../services/userProfileService';
import { UserProfileDto } from '../../../../../types/userProfile';
import { getProductImage } from '../../../../../constants/productImages';
import { PickingLine, GS1Data, SplitAllocation } from '../types';

// Extended picking line with additional UI properties
export interface ExtendedPickingLine extends PListLineDto {
  isPartiallyPicked?: boolean;
  isFullyPicked?: boolean;
  image?: string;
  requireLocationScan?: boolean;
  requireItemScan?: boolean;
  palletId?: string; // Now refers to SSCC
  itemDescription?: string;
  location?: string;
}

export interface UsePickingDataReturn {
  // Core data
  pickingDetails: PListDetailsDto | null;
  pickingLines: ExtendedPickingLine[];
  setPickingLines: React.Dispatch<React.SetStateAction<ExtendedPickingLine[]>>;
  loading: boolean;
  error: string | null;
  userProfile: UserProfileDto | null;
  pListOrders: PListOrderDto[];
  setPListOrders: React.Dispatch<React.SetStateAction<PListOrderDto[]>>;

  // Line tracking
  activeLineIndex: number | null;
  setActiveLineIndex: React.Dispatch<React.SetStateAction<number | null>>;
  currentLineIndex: number | null;
  setCurrentLineIndex: React.Dispatch<React.SetStateAction<number | null>>;
  lineStartTimes: Map<number, string>;
  setLineStartTimes: React.Dispatch<React.SetStateAction<Map<number, string>>>;

  // Computed values
  activeLine: ExtendedPickingLine | null;
  currentLine: ExtendedPickingLine | null;
  completedLines: number;

  // Settings
  enableRapidScanning: boolean;
  setEnableRapidScanning: React.Dispatch<React.SetStateAction<boolean>>;
  autoShowImage: boolean;
  setAutoShowImage: React.Dispatch<React.SetStateAction<boolean>>;
  enableDragAndDrop: boolean;
  setEnableDragAndDrop: React.Dispatch<React.SetStateAction<boolean>>;
  zeroPointCountingEnabled: boolean;
  fewPointCountingThreshold: number;

  // Data loading functions
  loadPickingDetails: (profile?: UserProfileDto | null) => Promise<void>;
  loadPListOrders: () => Promise<void>;
  loadUserProfile: () => Promise<UserProfileDto | null>;

  // Helper functions
  reorganizeLines: (lines: ExtendedPickingLine[]) => ExtendedPickingLine[];
  findLineByItem: (itemNo: string) => ExtendedPickingLine | null;
  convertToPickingLine: (line: ExtendedPickingLine) => PickingLine;
  convertToPickingLineWithCheck: (line: ExtendedPickingLine | undefined) => PickingLine | null;
  getOrderLineFieldValue: (line: ExtendedPickingLine, fieldName?: string) => string | undefined;
  parseGS1Barcode: (barcode: string) => GS1Data;
}

export function usePickingData(
  pListId: number,
  palletSummaries: PalletSummaryDto[]
): UsePickingDataReturn {
  // Core data state
  const [pickingDetails, setPickingDetails] = useState<PListDetailsDto | null>(null);
  const [pickingLines, setPickingLines] = useState<ExtendedPickingLine[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState<number | null>(null);
  const [activeLineIndex, setActiveLineIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lineStartTimes, setLineStartTimes] = useState<Map<number, string>>(new Map());
  const [userProfile, setUserProfile] = useState<UserProfileDto | null>(null);
  const [pListOrders, setPListOrders] = useState<PListOrderDto[]>([]);

  // Settings
  const [enableRapidScanning, setEnableRapidScanning] = useState(false);
  const [autoShowImage, setAutoShowImage] = useState(false);
  const [enableDragAndDrop, setEnableDragAndDrop] = useState(false);
  const [zeroPointCountingEnabled, setZeroPointCountingEnabled] = useState(true);
  const [fewPointCountingThreshold, setFewPointCountingThreshold] = useState(0);

  // Computed values
  const activeLine = activeLineIndex !== null ? pickingLines[activeLineIndex] : null;
  const currentLine = currentLineIndex !== null ? pickingLines[currentLineIndex] : null;
  const completedLines = pickingLines.filter(line => line.isCompleted).length;

  // ─────────────────────────────────────────────────
  // Data Loading
  // ─────────────────────────────────────────────────

  const loadUserProfile = useCallback(async (): Promise<UserProfileDto | null> => {
    try {
      const profile = await userProfileService.getCurrentUserProfile();
      setUserProfile(profile);
      console.log('User profile loaded:', {
        barCodeConfirmItem: profile.barCodeConfirmItem,
        barCodeConfirmLocation: profile.barCodeConfirmLocation
      });
      return profile;
    } catch (error) {
      console.warn('Could not load user profile, using default barcode settings:', error);
      return null;
    }
  }, []);

  const loadPickingDetails = useCallback(async (profile?: UserProfileDto | null) => {
    try {
      setLoading(true);
      setError(null);
      const details = await pickingService.getPickingDetails(pListId);
      setPickingDetails(details);

      // Fetch item descriptions from Items table
      const itemDescriptions = new Map<string, string>();
      const itemNos = details.lines.map(line => line.itemNo).filter(Boolean) as string[];
      const uniqueItemNos = Array.from(new Set(itemNos));

      for (const itemNo of uniqueItemNos) {
        try {
          const item = await itemsService.getItemByCompanyAndItemNo(details.header.companyNo, itemNo!);
          if (item.description1) {
            itemDescriptions.set(itemNo!, item.description1);
          }
        } catch (error) {
          console.warn(`Could not fetch description for item ${itemNo}:`, error);
        }
      }

      // Convert to ExtendedPickingLine format with additional properties
      const effectiveProfile = profile !== undefined ? profile : userProfile;
      const requireLocationScan = effectiveProfile?.barCodeConfirmLocation ?? false;
      const requireItemScan = effectiveProfile?.barCodeConfirmItem ?? false;

      const lines: ExtendedPickingLine[] = details.lines.map((line, index) => ({
        ...line,
        isCompleted: line.qtyPicked > 0,
        isPartiallyPicked: line.qtyPicked > 0 && line.qtyPicked < line.qty,
        isFullyPicked: line.qtyPicked >= line.qty,
        requireLocationScan: requireLocationScan,
        requireItemScan: requireItemScan,
        image: Math.random() > 0.7 ? getProductImage(index) : undefined,
        itemDescription: line.itemNo ? (itemDescriptions.get(line.itemNo) || 'Ingen beskrivelse') : 'Ukjent vare',
        // K5: Prefer human-readable bin description (StcBin.Description1) over raw BinCode.
        // Barcode validation in the active picking flow still uses binCode unchanged.
        location: line.binDescription1 || line.binCode || 'A-01-01',
        orderNoERP: line.orderNoERP,
        customerName: line.customerName
      }));

      // Sort by Sorting field (ascending)
      const sortedLines = lines.sort((a, b) => a.sorting - b.sorting);

      // Find first incomplete and check if there are completed lines
      const firstIncompleteIndex = sortedLines.findIndex(line => !line.isCompleted);
      const hasCompletedLines = sortedLines.some(line => line.isCompleted);

      // Reorganize: ufullførte øverst, fullførte i bunn
      if (hasCompletedLines && firstIncompleteIndex !== -1) {
        const reorganized = reorganizeLines(sortedLines);
        setPickingLines(reorganized);
        setActiveLineIndex(0);
      } else {
        setPickingLines(sortedLines);
        setActiveLineIndex(firstIncompleteIndex !== -1 ? firstIncompleteIndex : null);
      }
    } catch (err) {
      console.error('Error loading picking details:', err);
      setError('Kunne ikke laste plukk-detaljer. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }, [pListId, userProfile]);

  const loadPListOrders = useCallback(async () => {
    try {
      const companyNo = pickingDetails!.header.companyNo;
      console.log('Loading PListOrders for PListID:', pListId, 'CompanyNo:', companyNo);
      const orders = await plistOrderService.getByPListId(companyNo, pListId);
      console.log('Loaded PListOrders:', orders);
      setPListOrders(orders);
    } catch (error) {
      console.error('Error loading PListOrders:', error);
    }
  }, [pListId, pickingDetails]);

  // ─────────────────────────────────────────────────
  // Initial data load + settings
  // ─────────────────────────────────────────────────

  useEffect(() => {
    const loadData = async () => {
      // Load rapid scanning setting (Profile -> System -> Default)
      try {
        const fastPickSetting = await settingsService.getEffectiveSetting('picking.fastpick');
        if (fastPickSetting?.currentValue) {
          const isEnabled = fastPickSetting.currentValue === 'true' || fastPickSetting.currentValue === '1';
          setEnableRapidScanning(isEnabled);
          console.log('Loaded picking.fastpick effective setting:', isEnabled, '(source:', fastPickSetting.effectiveSource + ')');
        }
      } catch (err) {
        console.warn('Could not load picking.fastpick setting, using default (false):', err);
      }

      // Load zero-point counting setting (system level)
      try {
        const zeroCountSetting = await settingsService.getEffectiveSetting('picking.ZeroCountOnOff');
        if (zeroCountSetting?.currentValue) {
          const isEnabled = zeroCountSetting.currentValue === 'true' || zeroCountSetting.currentValue === '1';
          setZeroPointCountingEnabled(isEnabled);
          console.log('Loaded picking.ZeroCountOnOff effective setting:', isEnabled, '(source:', zeroCountSetting.effectiveSource + ')');
        }
      } catch (err) {
        console.warn('Could not load picking.ZeroCountOnOff setting, using default (true):', err);
      }

      // Load few-point counting threshold setting (system level)
      try {
        const fewPointSetting = await settingsService.getEffectiveSetting('picking.FewPointCountingThreshold');
        if (fewPointSetting?.currentValue) {
          const threshold = parseInt(fewPointSetting.currentValue, 10);
          if (!isNaN(threshold) && threshold >= 0) {
            setFewPointCountingThreshold(threshold);
            console.log('Loaded picking.FewPointCountingThreshold effective setting:', threshold, '(source:', fewPointSetting.effectiveSource + ')');
          }
        }
      } catch (err) {
        console.warn('Could not load picking.FewPointCountingThreshold setting, using default (0):', err);
      }

      const profile = await loadUserProfile();
      await loadPickingDetails(profile);
    };
    loadData();
  }, [pListId]);

  // Load PListOrders after pickingDetails is loaded
  useEffect(() => {
    if (pickingDetails) {
      loadPListOrders();
    }
  }, [pickingDetails]);

  // Track StartDateTime when active line changes
  useEffect(() => {
    if (activeLineIndex === null || pickingLines.length === 0) {
      return;
    }

    const currentActiveLine = pickingLines[activeLineIndex];
    if (currentActiveLine) {
      const pListLineNo = currentActiveLine.pListLineNo;
      setLineStartTimes(prev => {
        const updated = new Map(prev);
        updated.set(pListLineNo, new Date().toISOString());
        return updated;
      });
      console.log(`StartDateTime set for line ${pListLineNo}:`, new Date().toISOString());
    }
  }, [activeLineIndex, pickingLines]);

  // ─────────────────────────────────────────────────
  // Helper Functions
  // ─────────────────────────────────────────────────

  const reorganizeLines = useCallback((lines: ExtendedPickingLine[]): ExtendedPickingLine[] => {
    const sortedLines = [...lines].sort((a, b) => a.sorting - b.sorting);
    const completed = sortedLines.filter(line => line.isCompleted);
    const uncompleted = sortedLines.filter(line => !line.isCompleted);

    if (uncompleted.length === 0) {
      return completed;
    }

    return [...uncompleted, ...completed];
  }, []);

  const findLineByItem = useCallback((itemNo: string): ExtendedPickingLine | null => {
    const itemNoLower = itemNo.toLowerCase();
    const cleanedItemNo = itemNo.replace(/^0+/, '') || itemNo;
    const cleanedItemNoLower = cleanedItemNo.toLowerCase();

    const matches = (line: ExtendedPickingLine): boolean => {
      const lineItemNo = line.itemNo?.toLowerCase();
      const lineBarCode = line.itemBarCode?.toLowerCase();

      if (lineItemNo && (lineItemNo === itemNoLower || lineItemNo === cleanedItemNoLower)) {
        return true;
      }
      if (lineBarCode && (lineBarCode === itemNoLower || lineBarCode === cleanedItemNoLower)) {
        return true;
      }
      return false;
    };

    const incompleteLine = pickingLines.find(line => matches(line) && !line.isCompleted);
    if (incompleteLine) {
      return incompleteLine;
    }

    return pickingLines.find(line => matches(line)) || null;
  }, [pickingLines]);

  const parseGS1Barcode = useCallback((barcode: string): GS1Data => {
    const gs1Data: GS1Data = {};

    const patterns = {
      sscc: /\(00\)(\d{18})/,
      itemNo: /\(01\)([^()]+?)(?=\(|$)/,
      quantity: /\(30\)(\d+)/,
      batch: /\(10\)([^()]+?)(?=\(|$)/,
      expiry: /\(17\)(\d{6})/
    };

    Object.entries(patterns).forEach(([key, pattern]) => {
      const match = barcode.match(pattern);
      if (match) {
        if (key === 'quantity') {
          gs1Data[key] = parseInt(match[1]);
        } else if (key === 'itemNo') {
          const gtin = match[1];
          const varMatch = gtin.match(/VAR\d+/);
          if (varMatch) {
            gs1Data[key] = varMatch[0];
          } else {
            const trimmed = gtin.replace(/^0+/, '') || gtin.slice(-6);
            gs1Data[key] = trimmed;
          }
        } else {
          gs1Data[key as keyof GS1Data] = match[1] as any;
        }
      }
    });

    return gs1Data;
  }, []);

  // Lookup OrderLine field value by field name from UserProfile settings
  const getOrderLineFieldValue = useCallback((line: ExtendedPickingLine, fieldName?: string): string | undefined => {
    if (!fieldName) return undefined;
    const normalized = fieldName.toLowerCase().trim();
    const fieldMap: Record<string, string | undefined> = {
      'orderlinefreefield1': line.orderLineFreeField1,
      'orderlinefreefield2': line.orderLineFreeField2,
      'orderlinefreefield3': line.orderLineFreeField3,
      'orderlinefreefield4': line.orderLineFreeField4,
      'orderlinefreefield5': line.orderLineFreeField5,
      'orderlinefreefield6': line.orderLineFreeField6,
      'orderlinefreefield7': line.orderLineFreeField7,
      'orderlinefreefield8': line.orderLineFreeField8,
      'orderlinefreefield9': line.orderLineFreeField9,
      'orderlinefreefield10': line.orderLineFreeField10,
      'description1': line.orderLineDescription1,
      'operatortxt': line.orderLineOperatorTxt,
      'batchid': line.orderLineBatchID,
      'buyersitemno': line.orderLineBuyersItemNo,
      'itemgroup1': line.orderLineItemGroup1,
      'itemgroup2': line.orderLineItemGroup2,
      'itemgroup3': line.orderLineItemGroup3,
      'itemtemperatureinfo': line.orderLineItemTemperatureInfo,
      'uncode': line.orderLineUNCode,
      'memo1': line.orderLineMemo1,
    };
    return fieldMap[normalized];
  }, []);

  // Convert ExtendedPickingLine to PickingLine for child components
  const convertToPickingLine = useCallback((line: ExtendedPickingLine): PickingLine => {
    const splitAllocations = palletSummaries
      .filter(pallet => pallet.items.some(item => item.lineNo === line.pListLineNo))
      .map(pallet => ({
        palletId: pallet.sscc,
        qty: pallet.items
          .filter(item => item.lineNo === line.pListLineNo)
          .reduce((sum, item) => sum + item.quantity, 0)
      }))
      .filter(allocation => allocation.qty > 0);

    return {
      pListLineNo: line.pListLineNo,
      itemNo: line.itemNo || '',
      itemDescription: line.itemDescription || `${line.itemNo || 'Unknown'} - Beskrivelse`,
      qty: line.qty,
      qtyPicked: line.qtyPicked,
      qtyReserved: line.qtyReserved,
      location: line.location || line.binDescription1 || line.binCode || '',
      orderNo: line.orderNo.toString(),
      orderNoERP: line.orderNoERP,
      customerName: line.customerName,
      isCompleted: line.isCompleted,
      isPartiallyPicked: line.isPartiallyPicked,
      isFullyPicked: line.isFullyPicked,
      image: line.image,
      requireLocationScan: line.requireLocationScan,
      requireItemScan: line.requireItemScan,
      palletId: line.palletId,
      splitAllocations: splitAllocations.length > 0 ? splitAllocations : undefined,
      // Item type and Serial/Batch tracking
      itemType: line.itemType,
      serialNoItemYesNo: line.serialNoItemYesNo,
      batchItemYesNo: line.batchItemYesNo,
      serialNumberCount: line.serialNumbers?.length || 0,
      batchNumberCount: line.batchNumbers?.length || 0,
      extraInfoField2Value: getOrderLineFieldValue(line, userProfile?.userdefinedExtraInfoField2),
      extraInfoField3Value: getOrderLineFieldValue(line, userProfile?.userdefinedExtraInfoField3),
    };
  }, [palletSummaries, userProfile, getOrderLineFieldValue]);

  const convertToPickingLineWithCheck = useCallback((line: ExtendedPickingLine | undefined): PickingLine | null => {
    if (!line) return null;
    return convertToPickingLine(line);
  }, [convertToPickingLine]);

  return {
    // Core data
    pickingDetails,
    pickingLines,
    setPickingLines,
    loading,
    error,
    userProfile,
    pListOrders,
    setPListOrders,

    // Line tracking
    activeLineIndex,
    setActiveLineIndex,
    currentLineIndex,
    setCurrentLineIndex,
    lineStartTimes,
    setLineStartTimes,

    // Computed values
    activeLine,
    currentLine,
    completedLines,

    // Settings
    enableRapidScanning,
    setEnableRapidScanning,
    autoShowImage,
    setAutoShowImage,
    enableDragAndDrop,
    setEnableDragAndDrop,
    zeroPointCountingEnabled,
    fewPointCountingThreshold,

    // Data loading functions
    loadPickingDetails,
    loadPListOrders,
    loadUserProfile,

    // Helper functions
    reorganizeLines,
    findLineByItem,
    convertToPickingLine,
    convertToPickingLineWithCheck,
    getOrderLineFieldValue,
    parseGS1Barcode,
  };
}
